/**
 * The signed-in user's own GitHub connection.
 *
 * Separate from `useAuth` because it changes independently: a user can connect, revoke and
 * reconnect GitHub several times inside one session, and the sign-in state should not be
 * refetched every time they do.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { integrationsApi } from '@/lib/api/integrations'
import { queryKeys } from '@/lib/queryClient'

export function useGitHubConnection() {
  return useQuery({
    queryKey: queryKeys.githubConnection,
    queryFn: integrationsApi.status,
    staleTime: 30_000,
  })
}

export function useConnectGitHub() {
  return useMutation({
    mutationFn: (redirectTo?: string) => integrationsApi.connect(redirectTo),
    onSuccess: ({ authorize_url }) => {
      // A full navigation, not a fetch: the user has to authorize on github.com in their
      // own browser, signed in as themselves. The code comes back to the server, which
      // exchanges it out of band — so no GitHub token ever reaches this page.
      window.location.assign(authorize_url)
    },
  })
}

export function useDisconnectGitHub() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: integrationsApi.disconnect,
    onSuccess: async () => {
      // Every GitHub-derived view in the cache was fetched with the credential just
      // removed. Clearing the lot is right: leaving a repository list on screen after
      // disconnecting would show data the app can no longer fetch.
      queryClient.clear()
      await queryClient.invalidateQueries()
    },
  })
}
